import React, { useMemo, useState } from 'react';

import dayjs from '~/timeInit';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Card, CardContent, Collapse, Grid, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material';

export type LuxPlusRenewalModalProps = {
  customerId: string;
  subscriptionExpiryDate?: string | Date | null;
  totalSaved: number;
  profit: number;
  hasLuxPlusSubscription: boolean;
};

const collapsedCustomers = new Map<string, boolean>();

const formatCurrency = (value: number) => `$${value.toFixed(2)}`;

function LuxPlusRenewalModal({
  customerId,
  subscriptionExpiryDate,
  totalSaved,
  profit,
  hasLuxPlusSubscription,
}: LuxPlusRenewalModalProps) {
  const theme = useTheme();
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => collapsedCustomers.get(customerId) ?? false);

  const expiryDate = useMemo(() => dayjs(subscriptionExpiryDate ?? null).startOf('day'), [subscriptionExpiryDate]);
  const today = useMemo(() => dayjs().startOf('day'), []);
  const daysFromExpiry = expiryDate.isValid() ? today.diff(expiryDate, 'day') : null;

  const handleToggleCollapse = () => {
    const next = !isCollapsed;
    collapsedCustomers.set(customerId, next);
    setIsCollapsed(next);
  };

  if (
    !hasLuxPlusSubscription ||
    !expiryDate.isValid() ||
    daysFromExpiry === null ||
    daysFromExpiry < -14 ||
    daysFromExpiry > 14
  ) {
    return null;
  }

  const benefitItems = [
    'Unlock exclusive hotel, tour & experience savings',
    'Enjoy bonus perks: hotel credits, upgrades, daily breakfast & more',
    'Be first in line for our bestselling + hidden LuxPlus+-only deals',
    'Access growing partner perks and special offers',
  ];

  const retentionOffers = ['7,500 Société points + 100 Status Credits', '$20 Promo Credit'];

  return (
    <Card
      sx={{
        position: 'relative',
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderRadius: 3,
        boxShadow: theme.shadows[4],
        mb: 3,
        border: `1px solid ${theme.palette.grey[200]}`,
      }}
      data-testid="luxplus-renewal-modal"
    >
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} mb={2}>
          <Typography variant="h5" fontWeight={700}>
            Member Snapshot
          </Typography>
          <IconButton
            aria-label={isCollapsed ? 'Expand member snapshot' : 'Collapse member snapshot'}
            onClick={handleToggleCollapse}
          >
            {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
        </Stack>

        <Grid container spacing={2} mb={isCollapsed ? 0 : 2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              sx={{
                backgroundColor: theme.palette.grey[50],
                borderRadius: 2,
                border: `1px solid ${theme.palette.grey[200]}`,
                boxShadow: 'none',
              }}
            >
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Total Saved
                </Typography>
                <Typography variant="h4" color="success.main" fontWeight={700}>
                  {formatCurrency(totalSaved)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              sx={{
                backgroundColor: theme.palette.grey[50],
                borderRadius: 2,
                border: `1px solid ${theme.palette.grey[200]}`,
                boxShadow: 'none',
              }}
            >
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Profit from Membership
                  </Typography>
                  <Tooltip title="Profit from Membership = Total Saved - Membership Fee">
                    <InfoOutlinedIcon fontSize="small" color="action" />
                  </Tooltip>
                </Stack>
                <Typography variant="h4" color={profit > 0 ? 'success.main' : 'text.primary'} fontWeight={700}>
                  {formatCurrency(profit)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                sx={{
                  backgroundColor: theme.palette.grey[50],
                  color: theme.palette.text.primary,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.grey[200]}`,
                  boxShadow: 'none',
                  height: '100%',
                }}
              >
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Why Stay LuxPlus+?
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Remind the member of these key benefits
                  </Typography>
                  <Stack spacing={1} mt={1}>
                    {benefitItems.map((item) => (
                      <Stack key={item} direction="row" alignItems="center" spacing={1}>
                        <CheckCircleOutlineIcon fontSize="small" color="primary" />
                        <Typography variant="body2" color="text.primary">
                          {item}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                sx={{
                  backgroundColor: theme.palette.grey[50],
                  color: theme.palette.text.primary,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.grey[200]}`,
                  boxShadow: 'none',
                  height: '100%',
                }}
              >
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Retention Offers Available
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Before processing cancellation, consider offering:
                  </Typography>
                  <Stack spacing={1} mt={1}>
                    {retentionOffers.map((offer) => (
                      <Stack key={offer} direction="row" alignItems="center" spacing={1}>
                        <CheckCircleOutlineIcon fontSize="small" color="primary" />
                        <Typography variant="body2" color="text.primary">
                          {offer}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Collapse>
      </CardContent>
    </Card>
  );
}

export default LuxPlusRenewalModal;
